import { EditorOnly } from '../components/EditorOnly';
import { Icon } from '@iconify/react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
} from '@mui/material';
import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, api } from '../api/client';
import type {
  RankDashboard,
  RankKeywordRow,
  RankObservation,
  RankProjectDetail,
  RankProjectSummary,
  RankRun,
  RankTargetCheck,
} from '../api/types';
import { EmptyState } from '../components/EmptyState';
import { Box, Grid, Stack, Typography } from '../components/mui-system';
import { PageHeader } from '../components/PageHeader';
import { useWorkspace } from '../workspace-context';

type View = 'overview' | 'project' | 'settings';
type Sort = 'position' | 'delta' | 'query';

const bucketLabels = ['1–3', '4–5', '6–10', '11–30', '31–100'];
const bucketColors = ['success.main', 'secondary.main', 'warning.main', '#df9551', 'error.main'];

export function PositionsPage() {
  const { activeWorkspace } = useWorkspace();
  const workspaceId = activeWorkspace!.id;
  const [dashboard, setDashboard] = useState<RankDashboard | null>(null);
  const [runs, setRuns] = useState<RankRun[]>([]);
  const [detail, setDetail] = useState<RankProjectDetail | null>(null);
  const [view, setView] = useState<View>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [keywordsOpen, setKeywordsOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);

  const loadDashboard = useCallback(async () => {
    try {
      const next = await api.rankDashboard(workspaceId);
      setDashboard(next);
      setError('');
      return next;
    } catch (caught) {
      setError(errorMessage(caught));
      return null;
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    setLoading(true);
    setDetail(null);
    setView('overview');
    void Promise.all([
      loadDashboard(),
      api
        .rankRuns(workspaceId)
        .then(setRuns)
        .catch(() => setRuns([])),
    ]);
  }, [loadDashboard, workspaceId]);

  useEffect(() => {
    if (!dashboard?.activeRun) return;
    const timer = window.setInterval(async () => {
      const next = await loadDashboard();
      if (!next?.activeRun) {
        setRuns(await api.rankRuns(workspaceId).catch(() => []));
        if (detail) setDetail(await api.rankProject(workspaceId, detail.id).catch(() => detail));
        setNotice('Съём завершён, данные обновлены.');
      }
    }, 1_500);
    return () => window.clearInterval(timer);
  }, [dashboard?.activeRun, detail, loadDashboard, workspaceId]);

  async function openProject(projectId: string) {
    setActionBusy(true);
    setError('');
    try {
      setDetail(await api.rankProject(workspaceId, projectId));
      setView('project');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setActionBusy(false);
    }
  }

  async function startPrimary() {
    setActionBusy(true);
    setNotice('');
    setError('');
    try {
      await api.startRankRun(workspaceId);
      await loadDashboard();
      setNotice('Плановый съём через XMLStock поставлен в очередь.');
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setActionBusy(false);
    }
  }

  async function startInstant() {
    if (!detail) return;
    const estimatedSearches = detail.keywordCount * Math.ceil(detail.depth / 20);
    if (
      !window.confirm(
        `Запустить свежую проверку ${detail.keywordCount} запросов через SerpApi? Оценочно будет использовано ${estimatedSearches} поисковых запросов API.`,
      )
    ) {
      return;
    }
    setActionBusy(true);
    setNotice('');
    setError('');
    try {
      await api.startInstantRankRun(workspaceId, detail.id);
      await loadDashboard();
      setNotice('Моментальная проверка через SerpApi поставлена в очередь.');
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setActionBusy(false);
    }
  }

  async function created(project: RankProjectDetail) {
    setCreateOpen(false);
    await loadDashboard();
    setDetail(project);
    setView('project');
    setNotice(`Проект ${project.domain} добавлен.`);
  }

  async function keywordsAdded(message: string) {
    if (!detail) return;
    setKeywordsOpen(false);
    setDetail(await api.rankProject(workspaceId, detail.id));
    await loadDashboard();
    setNotice(message);
  }

  async function archiveProject() {
    if (
      !detail ||
      !window.confirm(`Убрать ${detail.domain} из мониторинга позиций? История сохранится.`)
    )
      return;
    setActionBusy(true);
    try {
      await api.archiveRankProject(workspaceId, detail.id);
      setDetail(null);
      setView('overview');
      await loadDashboard();
      setNotice('Проект архивирован, история сохранена.');
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setActionBusy(false);
    }
  }

  async function removeKeyword(keywordId: string) {
    if (!detail) return;
    try {
      await api.removeRankKeyword(workspaceId, detail.id, keywordId);
      setDetail(await api.rankProject(workspaceId, detail.id));
      await loadDashboard();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }

  async function refreshTargetCheck() {
    if (!detail) return;
    setActionBusy(true);
    setError('');
    try {
      const targetCheck = await api.refreshRankTargetCheck(workspaceId, detail.id);
      setDetail({ ...detail, targetCheck });
      await loadDashboard();
      setNotice('Проверка индексируемости домена обновлена.');
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setActionBusy(false);
    }
  }

  const title =
    view === 'project' && detail
      ? detail.domain
      : view === 'settings'
        ? 'Настройки позиций'
        : 'Мониторинг позиций';
  const description =
    view === 'project' && detail
      ? `${detail.name} · ${detail.regionName} · ${detail.device === 'desktop' ? 'Desktop' : 'Mobile'} · Top‑${detail.depth}`
      : view === 'settings'
        ? 'Источники данных и история реальных запусков.'
        : 'Точная история поисковых позиций по вашим доменам и запросам.';

  return (
    <>
      <PageHeader
        title={title}
        description={description}
        action={
          <>
            {view === 'project' && detail && (
              <Tooltip
                title={
                  dashboard?.providers.serpapi
                    ? 'Свежая независимая проверка без изменения плановой истории'
                    : 'Добавьте SERPAPI_KEY на сервере'
                }
              >
                <span>
                  <EditorOnly permissions="positions.run">
                    <Button
                      variant="outlined"
                      onClick={() => void startInstant()}
                      disabled={
                        actionBusy || !dashboard?.providers.serpapi || Boolean(dashboard?.activeRun)
                      }
                      startIcon={<Icon icon="solar:bolt-circle-linear" />}
                    >
                      Проверить сейчас · SerpApi
                    </Button>
                  </EditorOnly>
                </span>
              </Tooltip>
            )}
            <EditorOnly permissions="positions.create">
              <Button
                variant="contained"
                onClick={() => setCreateOpen(true)}
                startIcon={<Icon icon="solar:add-circle-linear" />}
              >
                Добавить проект
              </Button>
            </EditorOnly>
          </>
        }
      />

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 2 }}>
          {notice}
        </Alert>
      )}
      {dashboard?.activeRun && <RunProgress run={dashboard.activeRun} />}

      <Card sx={{ mb: 2.5 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ md: 'center' }}
          justifyContent="space-between"
          px={1.4}
        >
          <Tabs
            value={view === 'settings' ? 'settings' : 'overview'}
            onChange={(_, value: 'overview' | 'settings') => {
              setView(value);
              if (value === 'overview') setDetail(null);
            }}
          >
            <Tab value="overview" label="Сводка" />
            <Tab value="settings" label="Настройки и запуски" />
          </Tabs>
          <Stack direction="row" gap={0.8} alignItems="center" px={1} pb={{ xs: 1.4, md: 0 }}>
            <ProviderStatus configured={Boolean(dashboard?.providers.xmlstock)} label="XMLStock" />
            <ProviderStatus configured={Boolean(dashboard?.providers.serpapi)} label="SerpApi" />
          </Stack>
        </Stack>
      </Card>

      {loading && <LoadingState />}
      {!loading && view === 'overview' && dashboard && (
        <Overview
          dashboard={dashboard}
          busy={actionBusy}
          onOpen={(id) => void openProject(id)}
          onCreate={() => setCreateOpen(true)}
          onRun={() => void startPrimary()}
        />
      )}
      {!loading && view === 'project' && detail && (
        <ProjectView
          project={detail}
          onBack={() => {
            setDetail(null);
            setView('overview');
          }}
          onAddKeywords={() => setKeywordsOpen(true)}
          onArchive={() => void archiveProject()}
          onRemoveKeyword={(id) => void removeKeyword(id)}
          onRefreshTarget={() => void refreshTargetCheck()}
          targetCheckBusy={actionBusy}
        />
      )}
      {!loading && view === 'settings' && dashboard && (
        <Settings dashboard={dashboard} runs={runs} />
      )}

      <CreateProjectDialog
        open={createOpen}
        workspaceId={workspaceId}
        suggestedName={activeWorkspace!.name}
        suggestedDomain={activeWorkspace!.siteUrl}
        onClose={() => setCreateOpen(false)}
        onCreated={(project) => void created(project)}
      />
      {detail && (
        <AddKeywordsDialog
          open={keywordsOpen}
          workspaceId={workspaceId}
          project={detail}
          onClose={() => setKeywordsOpen(false)}
          onAdded={(message) => void keywordsAdded(message)}
        />
      )}
    </>
  );
}

function Overview({
  dashboard,
  busy,
  onOpen,
  onCreate,
  onRun,
}: {
  dashboard: RankDashboard;
  busy: boolean;
  onOpen(id: string): void;
  onCreate(): void;
  onRun(): void;
}) {
  if (!dashboard.projects.length) {
    return (
      <Card>
        <CardContent>
          <EmptyState
            icon={<Icon icon="solar:radar-2-bold-duotone" />}
            title="Добавьте первый проект"
            description="Укажите домен, регион и запросы. Никакие демонстрационные сайты больше не подмешиваются."
            action={
              <EditorOnly permissions="positions.create">
                <Button
                  variant="contained"
                  onClick={onCreate}
                  startIcon={<Icon icon="solar:add-circle-linear" />}
                >
                  Добавить проект
                </Button>
              </EditorOnly>
            }
          />
        </CardContent>
      </Card>
    );
  }
  return (
    <>
      <Grid container spacing={1.5} mb={2.5}>
        <MetricCard
          label="Видимость"
          value={`${dashboard.summary.visibility.toFixed(1)}%`}
          hint={`${dashboard.summary.foundPositions} из ${dashboard.summary.checkedPositions} проверенных позиций`}
        />
        <MetricCard label="В Top‑3" value={dashboard.summary.top3} hint="по всем проектам" />
        <MetricCard label="В Top‑10" value={dashboard.summary.top10} hint="по всем проектам" />
        <MetricCard
          label="Динамика"
          value={`▲${dashboard.summary.up} · ▼${dashboard.summary.down}`}
          hint={
            dashboard.summary.errorCount
              ? `${dashboard.summary.errorCount} ошибок в последнем срезе`
              : 'к прошлому плановому срезу'
          }
        />
      </Grid>
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ sm: 'center' }}
            gap={1.5}
            p={2.5}
          >
            <Box>
              <Typography variant="h3">Отслеживаемые проекты</Typography>
              <Typography color="text.secondary" fontSize={12} mt={0.4}>
                Домены добавлены пользователем в этом workspace
              </Typography>
            </Box>
            <Tooltip
              title={
                dashboard.providers.xmlstock
                  ? ''
                  : 'Добавьте XMLSTOCK_USER и XMLSTOCK_KEY на сервере'
              }
            >
              <span>
                <EditorOnly permissions="positions.run">
                  <Button
                    variant="outlined"
                    onClick={onRun}
                    disabled={busy || !dashboard.providers.xmlstock || Boolean(dashboard.activeRun)}
                    startIcon={<Icon icon="solar:refresh-circle-linear" />}
                  >
                    Снять плановые позиции
                  </Button>
                </EditorOnly>
              </span>
            </Tooltip>
          </Stack>
          <Divider />
          <Stack divider={<Divider flexItem />}>
            {dashboard.projects.map((project) => (
              <ProjectRow key={project.id} project={project} onOpen={() => onOpen(project.id)} />
            ))}
          </Stack>
        </CardContent>
      </Card>
    </>
  );
}

function ProjectRow({ project, onOpen }: { project: RankProjectSummary; onOpen(): void }) {
  return (
    <Box
      component="button"
      onClick={onOpen}
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr auto',
          lg: 'minmax(220px,1.1fr) minmax(280px,1.5fr) 1fr auto',
        },
        width: '100%',
        gap: 2,
        alignItems: 'center',
        p: { xs: 2, md: 2.4 },
        border: 0,
        bgcolor: 'transparent',
        color: 'text.primary',
        textAlign: 'left',
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box>
        <Typography fontWeight={850}>{project.domain}</Typography>
        <Typography color="text.secondary" fontSize={11.5} mt={0.3}>
          {project.name} · найдено {project.foundKeywords} из {project.validKeywords}
        </Typography>
        {project.targetCheck?.status !== 'ready' && (
          <Typography color="warning.main" fontSize={10.5} mt={0.3}>
            {project.targetCheck
              ? 'Есть проблемы с индексируемостью'
              : 'Индексируемость ещё не проверена'}
          </Typography>
        )}
      </Box>
      <BucketStrip project={project} />
      <Stack direction="row" gap={1} display={{ xs: 'none', lg: 'flex' }}>
        <Chip
          label={`${project.visibility.toFixed(1)}% видимость`}
          color="secondary"
          variant="outlined"
        />
        <Chip label={`▲${project.up} · ▼${project.down}`} variant="outlined" />
        {project.errorCount > 0 && (
          <Chip label={`${project.errorCount} ошибок`} color="error" variant="outlined" />
        )}
      </Stack>
      <Icon icon="solar:alt-arrow-right-linear" width={20} />
    </Box>
  );
}

function BucketStrip({ project }: { project: Pick<RankProjectSummary, 'buckets'> }) {
  return (
    <Stack direction="row" gap={0.6} display={{ xs: 'none', lg: 'flex' }}>
      {project.buckets.map((value, index) => (
        <Box key={bucketLabels[index]} textAlign="center" minWidth={42}>
          <Typography fontWeight={850} fontSize={13}>
            {value}
          </Typography>
          <Box height={3} my={0.35} sx={{ borderRadius: 99, bgcolor: bucketColors[index] }} />
          <Typography color="text.secondary" fontSize={9.5}>
            {bucketLabels[index]}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

function ProjectView({
  project,
  onBack,
  onAddKeywords,
  onArchive,
  onRemoveKeyword,
  onRefreshTarget,
  targetCheckBusy,
}: {
  project: RankProjectDetail;
  onBack(): void;
  onAddKeywords(): void;
  onArchive(): void;
  onRemoveKeyword(id: string): void;
  onRefreshTarget(): void;
  targetCheckBusy: boolean;
}) {
  const [filter, setFilter] = useState('');
  const [sort, setSort] = useState<Sort>('position');
  const rows = useMemo(() => {
    const search = filter.trim().toLocaleLowerCase('ru');
    return project.keywords
      .filter((row) => row.query.toLocaleLowerCase('ru').includes(search))
      .toSorted((a, b) => {
        if (sort === 'query') return a.query.localeCompare(b.query, 'ru');
        if (sort === 'delta') return Math.abs(b.delta ?? 0) - Math.abs(a.delta ?? 0);
        return (a.current.rank ?? 1_000) - (b.current.rank ?? 1_000);
      });
  }, [filter, project.keywords, sort]);
  const historyDates = project.keywords[0]?.history ?? [];
  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1} mb={2}>
        <Button
          onClick={onBack}
          startIcon={<Icon icon="solar:arrow-left-linear" />}
          sx={{ alignSelf: 'flex-start' }}
        >
          Все проекты
        </Button>
        <Stack direction="row" gap={1}>
          <EditorOnly permissions="positions.import">
            <Button
              variant="outlined"
              onClick={onAddKeywords}
              startIcon={<Icon icon="solar:add-circle-linear" />}
            >
              Добавить запросы
            </Button>
          </EditorOnly>
          <EditorOnly permissions="positions.delete">
            <Tooltip title="Проект исчезнет из списка, история останется">
              <IconButton aria-label="Архивировать проект" color="error" onClick={onArchive}>
                <Icon icon="solar:archive-down-minimlistic-linear" />
              </IconButton>
            </Tooltip>
          </EditorOnly>
        </Stack>
      </Stack>
      <TargetCheckAlert
        check={project.targetCheck}
        busy={targetCheckBusy}
        onRefresh={onRefreshTarget}
      />
      {project.lastCapturedAt && (project.notFoundKeywords > 0 || project.errorCount > 0) && (
        <Alert severity={project.foundKeywords ? 'info' : 'warning'} sx={{ mb: 2 }}>
          Последний XMLStock-съём: найдено {project.foundKeywords} из {project.validKeywords}; не
          найдено в Top‑{project.depth}: {project.notFoundKeywords}
          {project.errorCount ? `; ошибок: ${project.errorCount}` : ''}.
        </Alert>
      )}
      <Grid container spacing={1.5} mb={2.5}>
        <MetricCard
          label="Видимость"
          value={`${project.visibility.toFixed(1)}%`}
          hint={signed(project.visibilityChange)}
        />
        <MetricCard
          label="Средняя позиция"
          value={project.averagePosition?.toFixed(1) ?? '—'}
          hint={`${project.foundKeywords} из ${project.validKeywords} найдено`}
        />
        <MetricCard
          label="Top‑10"
          value={project.buckets.slice(0, 3).reduce((sum, value) => sum + value, 0)}
          hint={project.errorCount ? `${project.errorCount} ошибок отдельно` : 'валидный съём'}
        />
        <MetricCard
          label="Динамика"
          value={`▲${project.up} · ▼${project.down}`}
          hint={project.fresh ? `+${project.fresh} новых` : 'без новых'}
        />
      </Grid>
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            gap={1.2}
            p={2.2}
            alignItems={{ md: 'center' }}
          >
            <TextField
              placeholder="Фильтр по запросу"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              slotProps={{
                input: { startAdornment: <Icon icon="solar:magnifer-linear" width={18} /> },
              }}
              sx={{ minWidth: { md: 280 } }}
            />
            <TextField
              select
              value={sort}
              onChange={(event) => setSort(event.target.value as Sort)}
            >
              <MenuItem value="position">По позиции</MenuItem>
              <MenuItem value="delta">По динамике</MenuItem>
              <MenuItem value="query">По алфавиту</MenuItem>
            </TextField>
            <Typography color="text.secondary" fontSize={12} ml={{ md: 'auto' }}>
              показано {rows.length} из {project.keywords.length}
            </Typography>
          </Stack>
          <Divider />
          {project.keywords.length === 0 ? (
            <Box p={2.5}>
              <EmptyState
                icon={<Icon icon="solar:list-check-minimalistic-linear" />}
                title="Нет поисковых запросов"
                description="Добавьте фразы вручную или загрузите TXT/CSV, после чего можно запускать съём."
                action={
                  <EditorOnly permissions="positions.import">
                    <Button variant="contained" onClick={onAddKeywords}>
                      Добавить запросы
                    </Button>
                  </EditorOnly>
                }
              />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Запрос</TableCell>
                    <TableCell align="center">Поз.</TableCell>
                    <TableCell align="center">Δ</TableCell>
                    {historyDates.slice(-5).map((item) => (
                      <TableCell key={item.runId} align="center">
                        {shortDate(item.capturedAt)}
                      </TableCell>
                    ))}
                    <TableCell align="center">SerpApi сейчас</TableCell>
                    <TableCell>URL в выдаче</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <KeywordRow
                      key={row.id}
                      row={row}
                      depth={project.depth}
                      onRemove={() => onRemoveKeyword(row.id)}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function KeywordRow({
  row,
  depth,
  onRemove,
}: {
  row: RankKeywordRow;
  depth: number;
  onRemove(): void;
}) {
  return (
    <TableRow hover>
      <TableCell sx={{ minWidth: 200, fontWeight: 700 }}>{row.query}</TableCell>
      <TableCell align="center">
        <RankPill observation={row.current} depth={depth} />
      </TableCell>
      <TableCell align="center" sx={{ color: deltaColor(row.delta), fontWeight: 800 }}>
        {formatDelta(row.delta)}
      </TableCell>
      {row.history.slice(-5).map((item) => (
        <TableCell key={item.runId} align="center" sx={{ color: 'text.secondary' }}>
          <HistoryValue observation={item} depth={depth} />
        </TableCell>
      ))}
      <TableCell align="center">
        <RankPill observation={row.instant} depth={depth} instant />
      </TableCell>
      <TableCell sx={{ maxWidth: 260 }}>
        <Typography noWrap fontSize={11.5} color="text.secondary">
          {row.current.url ?? row.instant.url ?? '—'}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <EditorOnly permissions="positions.delete">
          <Tooltip title="Перестать отслеживать">
            <IconButton size="small" aria-label={`Удалить запрос ${row.query}`} onClick={onRemove}>
              <Icon icon="solar:trash-bin-minimalistic-linear" width={17} />
            </IconButton>
          </Tooltip>
        </EditorOnly>
      </TableCell>
    </TableRow>
  );
}

function TargetCheckAlert({
  check,
  busy,
  onRefresh,
}: {
  check: RankTargetCheck | null;
  busy: boolean;
  onRefresh(): void;
}) {
  const action = (
    <EditorOnly permissions="positions.run">
      <Button color="inherit" size="small" disabled={busy} onClick={onRefresh}>
        {busy ? 'Проверяем…' : check ? 'Перепроверить' : 'Проверить'}
      </Button>
    </EditorOnly>
  );
  if (!check) {
    return (
      <Alert severity="info" action={action} sx={{ mb: 2 }}>
        Домен создан до появления диагностики. Проверьте редиректы, canonical и доступ YandexBot.
      </Alert>
    );
  }
  if (check.status === 'ready') {
    return (
      <Alert severity="success" action={action} sx={{ mb: 2 }}>
        Домен отвечает без смены хоста, canonical согласован, YandexBot не заблокирован.
      </Alert>
    );
  }
  return (
    <Alert severity={check.status === 'error' ? 'error' : 'warning'} action={action} sx={{ mb: 2 }}>
      <Typography fontWeight={800} fontSize={13} mb={0.4}>
        {check.status === 'error'
          ? 'Индексируемость домена не удалось проверить'
          : 'Домен может не появляться в Яндексе'}
      </Typography>
      {check.error && <Typography fontSize={12}>{check.error}</Typography>}
      {check.warnings.map((warning) => (
        <Typography key={warning.code} fontSize={12}>
          {warning.message}
        </Typography>
      ))}
    </Alert>
  );
}

function Settings({ dashboard, runs }: { dashboard: RankDashboard; runs: RankRun[] }) {
  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, lg: 4 }}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h3" mb={2}>
              Источники выдачи
            </Typography>
            <Stack divider={<Divider flexItem />}>
              <ProviderDefinition
                title="XMLStock"
                description="Основной плановый съём Яндекса раз в 48 часов"
                configured={dashboard.providers.xmlstock}
                variables="XMLSTOCK_USER + XMLSTOCK_KEY"
              />
              <ProviderDefinition
                title="SerpApi"
                description="Опциональная свежая проверка отдельного проекта"
                configured={dashboard.providers.serpapi}
                variables="SERPAPI_KEY"
              />
            </Stack>
            <Alert severity="info" sx={{ mt: 2 }}>
              SerpApi-срезы сохраняются отдельно и не меняют плановую динамику XMLStock.
            </Alert>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, lg: 8 }}>
        <Card>
          <CardContent sx={{ p: 0 }}>
            <Box p={2.5}>
              <Typography variant="h3">История запусков</Typography>
            </Box>
            <Divider />
            {runs.length ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Источник</TableCell>
                      <TableCell>Тип</TableCell>
                      <TableCell>Старт</TableCell>
                      <TableCell>Статус</TableCell>
                      <TableCell>Прогресс</TableCell>
                      <TableCell>Ошибки</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {runs.map((run) => (
                      <TableRow key={run.id} hover>
                        <TableCell>
                          {run.provider === 'xmlstock' ? 'XMLStock' : 'SerpApi'}
                        </TableCell>
                        <TableCell>{triggerLabel(run.trigger)}</TableCell>
                        <TableCell>{formatDate(run.startedAt ?? run.createdAt)}</TableCell>
                        <TableCell>
                          <RunStatus status={run.status} />
                        </TableCell>
                        <TableCell>
                          {run.processedKeywords}/{run.totalKeywords}
                        </TableCell>
                        <TableCell sx={{ color: run.errorCount ? 'error.main' : 'text.secondary' }}>
                          {run.errorCount}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box p={2.5}>
                <EmptyState
                  icon={<Icon icon="solar:history-linear" />}
                  title="Запусков пока нет"
                  description="История появится после первого планового или моментального съёма."
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

function CreateProjectDialog({
  open,
  workspaceId,
  suggestedName,
  suggestedDomain,
  onClose,
  onCreated,
}: {
  open: boolean;
  workspaceId: string;
  suggestedName: string;
  suggestedDomain: string;
  onClose(): void;
  onCreated(project: RankProjectDetail): void;
}) {
  const [name, setName] = useState(suggestedName);
  const [domain, setDomain] = useState(suggestedDomain);
  const [regionCode, setRegionCode] = useState('213');
  const [regionName, setRegionName] = useState('Москва');
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [depth, setDepth] = useState(100);
  const [keywordText, setKeywordText] = useState('');
  const [fileName, setFileName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function importFile(file?: File) {
    if (!file) return;
    setFileName(file.name);
    const content = await file.text();
    setKeywordText((current) => [current, ...parseKeywordFile(content)].filter(Boolean).join('\n'));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const project = await api.createRankProject(workspaceId, {
        name,
        domain,
        regionCode,
        regionName,
        device,
        depth,
        keywords: parseKeywords(keywordText),
      });
      onCreated(project);
      setKeywordText('');
      setFileName('');
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <Box component="form" onSubmit={(event: FormEvent) => void submit(event)}>
        <DialogTitle>Добавить проект в мониторинг позиций</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                required
                label="Название"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                required
                label="Домен или URL"
                value={domain}
                onChange={(event) => setDomain(event.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField
                fullWidth
                required
                label="Регион"
                value={regionName}
                onChange={(event) => setRegionName(event.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <TextField
                fullWidth
                required
                label="Yandex lr"
                value={regionCode}
                onChange={(event) => setRegionCode(event.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <TextField
                select
                fullWidth
                label="Устройство"
                value={device}
                onChange={(event) => setDevice(event.target.value as 'desktop' | 'mobile')}
              >
                <MenuItem value="desktop">Desktop</MenuItem>
                <MenuItem value="mobile">Mobile</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                select
                fullWidth
                label="Глубина"
                value={depth}
                onChange={(event) => setDepth(Number(event.target.value))}
              >
                <MenuItem value={10}>Top‑10</MenuItem>
                <MenuItem value={30}>Top‑30</MenuItem>
                <MenuItem value={50}>Top‑50</MenuItem>
                <MenuItem value={100}>Top‑100</MenuItem>
              </TextField>
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                multiline
                minRows={7}
                label="Поисковые запросы — по одному на строку"
                value={keywordText}
                onChange={(event) => setKeywordText(event.target.value)}
                helperText={`${parseKeywords(keywordText).length} уникальных запросов. Можно создать проект без запросов и добавить их позже.`}
              />
            </Grid>
            <Grid size={12}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<Icon icon="solar:upload-linear" />}
              >
                {fileName || 'Загрузить TXT или CSV'}
                <input
                  hidden
                  type="file"
                  accept=".txt,.csv,text/plain,text/csv"
                  onChange={(event) => void importFile(event.target.files?.[0])}
                />
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? 'Создаём…' : 'Создать проект'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

function AddKeywordsDialog({
  open,
  workspaceId,
  project,
  onClose,
  onAdded,
}: {
  open: boolean;
  workspaceId: string;
  project: RankProjectDetail;
  onClose(): void;
  onAdded(message: string): void;
}) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  async function importFile(file?: File) {
    if (!file) return;
    const imported = parseKeywordFile(await file.text());
    setText((current) => [current, ...imported].filter(Boolean).join('\n'));
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const result = await api.addRankKeywords(workspaceId, project.id, parseKeywords(text));
      setText('');
      onAdded(
        `Добавлено запросов: ${result.added}${result.restored ? `, восстановлено: ${result.restored}` : ''}.`,
      );
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  }
  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <Box component="form" onSubmit={(event: FormEvent) => void submit(event)}>
        <DialogTitle>Добавить запросы · {project.domain}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={9}
            label="По одному запросу на строку"
            value={text}
            onChange={(event) => setText(event.target.value)}
            helperText={`${parseKeywords(text).length} уникальных запросов`}
          />
          <Button
            component="label"
            variant="text"
            sx={{ mt: 1 }}
            startIcon={<Icon icon="solar:upload-linear" />}
          >
            Загрузить TXT или CSV
            <input
              hidden
              type="file"
              accept=".txt,.csv,text/plain,text/csv"
              onChange={(event) => void importFile(event.target.files?.[0])}
            />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving || !parseKeywords(text).length}
          >
            {saving ? 'Добавляем…' : 'Добавить'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

function RunProgress({ run }: { run: RankRun }) {
  const value = run.totalKeywords ? (run.processedKeywords / run.totalKeywords) * 100 : 0;
  return (
    <Card sx={{ mb: 2.5, borderColor: 'primary.main' }}>
      <CardContent sx={{ py: '14px !important' }}>
        <Stack direction="row" justifyContent="space-between" mb={1}>
          <Typography fontWeight={800} fontSize={13}>
            {run.provider === 'serpapi'
              ? 'Моментальная проверка SerpApi'
              : 'Плановый съём XMLStock'}
          </Typography>
          <Typography color="text.secondary" fontSize={12}>
            {run.processedKeywords} из {run.totalKeywords}
          </Typography>
        </Stack>
        <LinearProgress variant="determinate" value={value} sx={{ height: 6, borderRadius: 99 }} />
      </CardContent>
    </Card>
  );
}

function MetricCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string | number;
  hint: string;
}) {
  return (
    <Grid size={{ xs: 6, lg: 3 }}>
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography color="text.secondary" fontSize={11.5}>
            {label}
          </Typography>
          <Typography
            fontSize={{ xs: 23, md: 29 }}
            fontWeight={850}
            letterSpacing="-.04em"
            mt={0.5}
          >
            {value}
          </Typography>
          <Typography color="text.secondary" fontSize={10.5} mt={0.3}>
            {hint}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );
}

function ProviderStatus({ configured, label }: { configured: boolean; label: string }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      color={configured ? 'success' : 'default'}
      icon={
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: 99,
            bgcolor: configured ? 'success.main' : 'text.disabled',
          }}
        />
      }
      label={`${label}: ${configured ? 'готов' : 'не настроен'}`}
    />
  );
}

function ProviderDefinition({
  title,
  description,
  configured,
  variables,
}: {
  title: string;
  description: string;
  configured: boolean;
  variables: string;
}) {
  return (
    <Box py={1.6}>
      <Stack direction="row" justifyContent="space-between" gap={2}>
        <Box>
          <Typography fontWeight={800}>{title}</Typography>
          <Typography color="text.secondary" fontSize={11.5} mt={0.4}>
            {description}
          </Typography>
        </Box>
        <Chip
          size="small"
          color={configured ? 'success' : 'default'}
          variant="outlined"
          label={configured ? 'Подключён' : 'Не настроен'}
        />
      </Stack>
      {!configured && (
        <Typography component="code" color="text.secondary" fontSize={10.5} display="block" mt={1}>
          {variables}
        </Typography>
      )}
    </Box>
  );
}

function RunStatus({ status }: { status: RankRun['status'] }) {
  const color =
    status === 'complete'
      ? 'success'
      : status === 'partial'
        ? 'warning'
        : status === 'failed'
          ? 'error'
          : 'info';
  const labels = {
    queued: 'в очереди',
    running: 'идёт',
    complete: 'готов',
    partial: 'частично',
    failed: 'ошибка',
  };
  return <Chip size="small" color={color} variant="outlined" label={labels[status]} />;
}

function RankPill({
  observation,
  depth,
  instant = false,
}: {
  observation: RankObservation;
  depth: number;
  instant?: boolean;
}) {
  const rank = observation.rank;
  const label =
    observation.status === 'not_found'
      ? `>${depth}`
      : observation.status === 'error'
        ? 'Ошибка'
        : observation.status === 'not_collected'
          ? '—'
          : rank;
  return (
    <Tooltip title={observationExplanation(observation, depth)}>
      <Chip
        size="small"
        variant={instant ? 'outlined' : 'filled'}
        color={
          observation.status === 'error'
            ? 'error'
            : observation.status === 'not_found'
              ? 'default'
              : rank !== null && rank <= 10
                ? 'success'
                : rank !== null && rank <= 30
                  ? 'warning'
                  : 'default'
        }
        label={label}
        sx={{ minWidth: 38 }}
      />
    </Tooltip>
  );
}

function HistoryValue({ observation, depth }: { observation: RankObservation; depth: number }) {
  const label =
    observation.status === 'found'
      ? observation.rank
      : observation.status === 'not_found'
        ? `>${depth}`
        : observation.status === 'error'
          ? '!'
          : '—';
  return (
    <Tooltip title={observationExplanation(observation, depth)}>
      <Typography
        component="span"
        fontSize={12}
        fontWeight={observation.status === 'error' ? 900 : 600}
        color={observation.status === 'error' ? 'error.main' : 'text.secondary'}
      >
        {label}
      </Typography>
    </Tooltip>
  );
}

function observationExplanation(observation: RankObservation, depth: number): string {
  if (observation.status === 'error') return observation.error ?? 'Ошибка съёма без описания';
  if (observation.status === 'not_collected')
    return 'Для этого запроса ещё нет сопоставимого съёма';
  const evidence = observation.evidenceAvailable
    ? `Проверено результатов выдачи: ${observation.observedResults}.`
    : 'Список просмотренных результатов не сохранялся в этой старой версии съёма.';
  return observation.status === 'not_found'
    ? `Домен не найден в Top‑${depth}. ${evidence}`
    : `Позиция ${observation.rank}. ${evidence}`;
}

function LoadingState() {
  return (
    <Grid container spacing={1.5}>
      {[1, 2, 3, 4].map((item) => (
        <Grid key={item} size={{ xs: 6, lg: 3 }}>
          <Skeleton variant="rounded" height={118} />
        </Grid>
      ))}
    </Grid>
  );
}

function parseKeywords(value: string): string[] {
  return [
    ...new Set(
      value
        .split(/\r?\n/)
        .map((item) => item.trim().replace(/\s+/g, ' '))
        .filter(Boolean),
    ),
  ];
}

function parseKeywordFile(content: string): string[] {
  const lines = content
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const result: string[] = [];
  for (const [index, rawLine] of lines.entries()) {
    const line = rawLine.replace(/^\uFEFF/, '').trim();
    const firstCell = unquoteCsvCell(line.split(/[;\t,]/)[0]?.trim() ?? '');
    if (index === 0 && /^(query|keyword|запрос)$/i.test(firstCell)) continue;
    const task = line.match(/^[^:]+:https?:\/\/.*?:(.*?):\d*:([^:]*):([^:]*)$/i);
    if (task?.[1]) result.push(task[1]);
    else result.push(firstCell);
  }
  return parseKeywords(result.join('\n'));
}

function unquoteCsvCell(value: string): string {
  if (value.length >= 2 && value.startsWith('"') && value.endsWith('"')) {
    return value.slice(1, -1).replaceAll('""', '"').trim();
  }
  return value;
}

function errorMessage(error: unknown): string {
  return error instanceof ApiError || error instanceof Error
    ? error.message
    : 'Не удалось выполнить операцию';
}

function signed(value: number): string {
  if (!value) return 'без изменений';
  return `${value > 0 ? '▲' : '▼'}${Math.abs(value).toFixed(1)} п.п.`;
}

function formatDelta(value: number | null): string {
  if (value === null) return '—';
  if (!value) return '0';
  return `${value > 0 ? '▲' : '▼'}${Math.abs(value)}`;
}

function deltaColor(value: number | null): string {
  if (value === null || value === 0) return 'text.secondary';
  return value > 0 ? 'success.main' : 'error.main';
}

function shortDate(value: string | null): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit' }).format(
    new Date(value),
  );
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function triggerLabel(value: RankRun['trigger']): string {
  return value === 'scheduled' ? 'по расписанию' : value === 'instant' ? 'в моменте' : 'вручную';
}
